/**
 * Entity size formatting — lakehouse / warehouse "Size" display.
 *
 * The sizing investigation (2026-07-12) found "empty size" conflates three distinct states that
 * were all collapsing to a blank/0:
 *   - 0 tables            -> the entity is genuinely EMPTY (label it, don't blank it)
 *   - has data < 1 GB     -> real size floored to 0.00 GB by the coarse GB column -> show MB/KB/B
 *   - >= 1 GB             -> show GB/TB
 * `storage_size_gb` is decimal(18,2) so it loses tiny sizes; the fine-grained truth is the byte
 * sum from dbo.lakehouse_tables (which the collector itself sums into storage_size_gb). This helper
 * formats from bytes so the unit is always explicit and a GB->MB switch can never be mistaken.
 */

/** Human-readable byte size with an always-explicit unit (e.g. "1.19 MB", "280.5 GB", "0 B"). */
export function humanBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  // Bytes have no fractional part; otherwise 1 decimal for large magnitudes, 2 for small.
  const decimals = i === 0 ? 0 : v >= 100 ? 1 : 2;
  return `${v.toFixed(decimals)} ${units[i]}`;
}

export interface EntitySize {
  /** Display text: "Not measured", "Empty" for 0-table entities, otherwise a byte-derived size with explicit unit. */
  text: string;
  /** True when the entity has no tables (render muted/distinct so it isn't read as a measured 0). */
  isEmpty: boolean;
  /** True when the last collection did not size the entity (size_measurement_error set): its size inputs are old. */
  notMeasured: boolean;
  /** Exact byte count when known (for a tooltip), else null. */
  bytes: number | null;
}

/**
 * Resolve an entity's Size cell from its fine-grained inputs.
 * @param sizeBytes   SUM of active table bytes (null when no table inventory was collected)
 * @param tableCount  number of active tables (0 => Empty)
 * @param sizeGbFallback  storage_size_gb, used only when byte inventory is unavailable
 * @param sizeError   size_measurement_error: set when the last collection did not size the entity (Spec 286 T159
 *                    item 4). The other inputs are then old, so the cell reads "Not measured", never a size or "Empty".
 */
export function formatEntitySize(
  sizeBytes: number | null | undefined,
  tableCount: number | null | undefined,
  sizeGbFallback?: number | null,
  sizeError?: string | null
): EntitySize {
  if (sizeError) {
    return { text: 'Not measured', isEmpty: false, notMeasured: true, bytes: null };
  }
  const tables = Number(tableCount) || 0;

  // Prefer measured bytes; fall back to the coarse GB column only when inventory is missing.
  let bytes: number | null = sizeBytes == null ? null : Number(sizeBytes);
  if (bytes == null && sizeGbFallback != null && Number(sizeGbFallback) > 0) {
    bytes = Number(sizeGbFallback) * 1024 ** 3;
  }

  // 0 tables (and no residual measured size) => genuinely empty.
  if (tables === 0 && (bytes == null || bytes <= 0)) {
    return { text: 'Empty', isEmpty: true, notMeasured: false, bytes: bytes ?? null };
  }

  // Has tables but no inventory bytes at all => nothing to size against.
  if (bytes == null) {
    return { text: 'Empty', isEmpty: true, notMeasured: false, bytes: null };
  }

  // Has tables (or a residual size) => show the real, unit-explicit size (may be "0 B").
  return { text: humanBytes(bytes), isEmpty: false, notMeasured: false, bytes };
}

export interface WarehouseSize {
  text: string;
  state: 'sized' | 'measured-tiny' | 'empty' | 'not-measured';
  /** For not-measured, why (from size_measurement_error or a generic explanation). */
  reason?: string;
  /** For not-measured: the size the merge preserved from an earlier collection, or null when none above 0 is held. */
  lastKnownGb?: number | null;
}

// The subtext of a total that counts sizes the last collection did not measure: one definition, shared app-wide.
export { unmeasuredSizeNote } from '@/utils/unmeasuredSize';

/** The tooltip of a warehouse the last collection did not size (Spec 286 T159 item 32). */
export function warehouseSizeNote(size: WarehouseSize): string {
  const head = `The last collection did not measure this warehouse (${size.reason ?? 'no reason recorded'}).`;
  return size.lastKnownGb != null ? `${head} Last known size: ${humanBytes(size.lastKnownGb * 1024 ** 3)}.` : head;
}

/**
 * Warehouse Size cell. Warehouses have NO per-table byte inventory (warehouse_tables has no
 * size_bytes) and the Fabric DW size DMV is unsupported, so a `0` can mean three very different
 * things — the single unmeasured signal is `size_measurement_error` (Spec 238):
 *   - size > 0                                     -> the size (GB/TB, or MB for sub-GB)
 *   - error != NULL                                -> "Not measured" (collection failed/unreachable)
 *   - size = 0, error = NULL, 0 tables             -> "Empty" (measured, genuinely empty)
 *   - size = 0, error = NULL, tables > 0           -> "< 0.01 GB" (measured-tiny: real footprint
 *     under ~5 MB that rounds to 0.00 in the DECIMAL(18,2) column — FR-012; NOT "Not measured")
 * @param storageGb   dbo.warehouses.storage_size_gb
 * @param tableCount  active dbo.warehouse_tables rows
 * @param sizeError   dbo.warehouses.size_measurement_error (e.g. "no-endpoint: …")
 */
export function formatWarehouseSize(
  storageGb: number | null | undefined,
  tableCount: number | null | undefined,
  sizeError?: string | null
): WarehouseSize {
  const gb = Number(storageGb) || 0;
  const tables = Number(tableCount) || 0;
  const err = sizeError ? String(sizeError) : null;

  // The ONLY unmeasured signal is a recorded reason (Spec 238), and it wins over a size: the merge keeps the earlier
  // size when a collection cannot measure one, so that size is the last known one, not current (Spec 286 T159 item 32).
  if (err) {
    return { text: 'Not measured', state: 'not-measured', reason: err, lastKnownGb: gb > 0 ? gb : null };
  }
  if (gb > 0) {
    return { text: humanBytes(gb * 1024 ** 3), state: 'sized' };
  }
  // gb <= 0 with no reason: the warehouse WAS measured this run, just at 0.00 (genuinely empty, or under 0.01 GB).
  if (tables === 0) {
    return { text: 'Empty', state: 'empty' };
  }
  return { text: '< 0.01 GB', state: 'measured-tiny' };
}
